import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import TeamCard from './TeamCard'; // Import the TeamCard component

function TherapistDashboard() {
  const navigate = useNavigate();

  // POPUP MESSAGES
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [showPopup, setShowPopup] = useState(false);

  // THERAPIST STATS
  const [sessionStats, setSessionStats] = useState({
    lastWeekCount: 0,
    thisWeekCount: 0,
    monthlyAvgCount: 0,
    debtCount: 0,
    debtTotal: 0,
    noShowWeekCount: 0,
    noShowMonthCount: 0,
    cancelWeekCount: 0,
    cancelMonthCount: 0
  });

  // GOALS
  const [goals, setGoals] = useState({
    weekly_session_goal: 0,
    weekly_session_bonus: 0
  });

  // THERAPIST NAME
  const [therapistName, setTherapistName] = useState('');

  // NEW: THERAPIST PROFILE
  const [profile, setProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  // NEW: TEAM MEMBERS
  const [teamMembers, setTeamMembers] = useState([]);

  // --------------------------------------------------------------------------
  // 1) FETCH UNREAD MESSAGES
  // --------------------------------------------------------------------------
  const fetchUnreadMessages = async () => {
    try {
      // This route must return title, content, image_url, etc.
      const res = await API.get('/therapist/messages?unreadOnly=true');
      if (res.data && res.data.length > 0) {
        setUnreadMessages(res.data);
        setShowPopup(true);
      }
    } catch (error) {
      console.error('Error fetching unread messages:', error);
    }
  };

  const closePopup = async () => {
    try {
      // Mark them as read
      await Promise.all(
        unreadMessages.map((msg) =>
          API.put(`/therapist/messages/${msg.id}`, { isRead: true })
        )
      );
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
    setShowPopup(false);
  };

  // --------------------------------------------------------------------------
  // 2) FETCH STATS & GOALS
  // --------------------------------------------------------------------------
  const fetchSessionStats = async () => {
    try {
      const res = await API.get('/therapist/stats');
      const data = res.data || {};
      data.debtTotal = parseFloat(data.debtTotal) || 0;
      setSessionStats(data);
    } catch (error) {
      console.error('Error fetching session stats:', error);
    }
  };

  const fetchGoals = async () => {
    try {
      const res = await API.get('/therapist/goals');
      setGoals(res.data || {});
    } catch (err) {
      console.error('Error fetching therapist goals:', err);
    }
  };

  // --------------------------------------------------------------------------
  // 3) LOAD THERAPIST NAME FROM LOCALSTORAGE
  // --------------------------------------------------------------------------
  const loadTherapistName = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      setTherapistName(`${user.first_name} ${user.last_name}` || '');
    }
  };

  // --------------------------------------------------------------------------
  // 4) NEW: FETCH THERAPIST PROFILE
  // --------------------------------------------------------------------------
  const fetchTherapistProfile = async () => {
    try {
      const res = await API.get('/therapist/profile');
      setProfile(res.data);
    } catch (error) {
      console.error('Error fetching therapist profile:', error);
    }
  };
  
  // --------------------------------------------------------------------------
  // 5) NEW: FETCH TEAM MEMBERS
  // --------------------------------------------------------------------------
  const fetchTeamMembers = async () => {
    try {
      const res = await API.get('/therapist/team-profiles');
      setTeamMembers(res.data || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  // --------------------------------------------------------------------------
  // 6) NEW: HANDLE PROFILE EDIT MODAL
  // --------------------------------------------------------------------------
  const openProfileModal = () => {
    setShowProfileModal(true);
  };

  const closeProfileModal = () => {
    setShowProfileModal(false);
    // Refresh profile data after closing modal
    fetchTherapistProfile();
  };

  // --------------------------------------------------------------------------
  // useEffect (on mount)
  // --------------------------------------------------------------------------
  useEffect(() => {
    fetchUnreadMessages();
    fetchSessionStats();
    fetchGoals();
    loadTherapistName();
    fetchTherapistProfile();
    fetchTeamMembers(); // New: fetch team members
  }, []);

  // --------------------------------------------------------------------------
  // GREETING
  // --------------------------------------------------------------------------
  function getTimeOfDayGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }
  const greeting = `${getTimeOfDayGreeting()}, ${therapistName}!`;

  // --------------------------------------------------------------------------
  // HELPER: getSessionStyle(count)
  // --------------------------------------------------------------------------
  function getSessionStyle(count) {
    const bonus = goals.weekly_session_bonus || 0;
    if (bonus <= 0) {
      return {
        background: 'hsl(0, 0%, 95%)',
        showEmoji: false
      };
    }
    let ratio = count / bonus;
    if (ratio < 0) ratio = 0;
    if (ratio > 1) ratio = 1;

    const hue = 120 * ratio; // 0 => red, 120 => green
    const background = `hsl(${hue}, 60%, 80%)`;
    const showEmoji = count >= bonus;

    return { background, showEmoji };
  }

  // --------------------------------------------------------------------------
  // HELPER: getDebtBg(debtAmount)
  // --------------------------------------------------------------------------
  function getDebtBg(debtAmount) {
    if (debtAmount > 0) {
      return '#ff6666'; // darker red
    }
    // Green shade if zero debt
    return '#d4edda';
  }

  // --------------------------------------------------------------------------
  // HELPER: getNoShowStyle(count)
  // --------------------------------------------------------------------------
  function getNoShowStyle(count) {
    // For no-shows, lower is better
    if (count === 0) {
      return { background: '#d4edda' }; // Green
    } else if (count <= 2) {
      return { background: '#fff3cd' }; // Yellow warning
    } else {
      return { background: '#f8d7da' }; // Red danger
    }
  }

  // --------------------------------------------------------------------------
  // HELPER: getCancelStyle(count)
  // --------------------------------------------------------------------------
  function getCancelStyle(count) {
    // For cancellations, lower is better
    if (count === 0) {
      return { background: '#d4edda' }; // Green 
    } else if (count <= 3) {
      return { background: '#fff3cd' }; // Yellow warning
    } else {
      return { background: '#f8d7da' }; // Red danger
    }
  }

  // --------------------------------------------------------------------------
  // STATS
  // --------------------------------------------------------------------------
  const {
    lastWeekCount = 0,
    thisWeekCount = 0,
    monthlyAvgCount = 0,
    debtCount = 0,
    debtTotal = 0,
    noShowWeekCount = 0,
    noShowMonthCount = 0,
    cancelWeekCount = 0,
    cancelMonthCount = 0
  } = sessionStats;

  const lastWeekObj = getSessionStyle(lastWeekCount);
  const thisWeekObj = getSessionStyle(thisWeekCount);
  const avgObj = getSessionStyle(monthlyAvgCount);
  const noShowWeekObj = getNoShowStyle(noShowWeekCount);
  const cancelWeekObj = getCancelStyle(cancelWeekCount);

  const safeDebtTotal = parseFloat(debtTotal) || 0;
  const debtBg = getDebtBg(safeDebtTotal);
  const textColor = 'black';

  // progress bar for "This Week"
  const weeklyGoal = goals.weekly_session_goal || 1;
  let progressPercent = (thisWeekCount / weeklyGoal) * 100;
  if (progressPercent < 0) progressPercent = 0;
  if (progressPercent > 100) progressPercent = 100;

  // --------------------------------------------------------------------------
  // RETURN
  // --------------------------------------------------------------------------
  return (
    <div style={{ padding: '1rem' }}>
      {/* POPUP FOR UNREAD MESSAGES */}
      {showPopup && (
        <div style={popupStyle}>
          <h3>New Messages</h3>
          {unreadMessages.map((msg) => (
            <div key={msg.id} style={{ marginBottom: '1rem' }}>
              <h4>{msg.title}</h4>
              <p>{msg.content}</p>
              {/* IF image_url is present, display it */}
              {msg.image_url && (
                <img
                  src={msg.image_url}
                  alt="Admin Attachment"
                  style={{ maxWidth: '100px', marginTop: '0.5rem' }}
                />
              )}
            </div>
          ))}
          <button onClick={closePopup}>Close</button>
        </div>
      )}

      {/* NEW: PROFILE MODAL */}
      {showProfileModal && profile && (
        <ProfileEditModal 
          profile={profile} 
          onClose={closeProfileModal} 
        />
      )}

      <h2 style={{ marginBottom: '2rem' }}>{greeting}</h2>

      {/* PROFILE AND TEAM CARDS ROW */}
      <div style={cardsRowStyle}>
        {/* THERAPIST PROFILE CARD */}
        {profile && (
          <div 
            style={profileCardStyle}
            onClick={openProfileModal}
          >
            <div style={profileHeaderStyle}>
              <h3 style={profileTitleStyle}>Your Profile</h3>
              <div style={editProfileIndicatorStyle}>Click to edit</div>
            </div>
            
            <div style={profileContentContainerStyle}>
              {/* Profile Photo Column */}
              <div style={profilePhotoContainerStyle}>
                {profile.photo_url ? (
                  <img 
                    src={profile.photo_url} 
                    alt="Profile" 
                    style={profilePhotoStyle} 
                  />
                ) : (
                  <div style={{
                    ...profilePhotoStyle,
                    backgroundColor: '#e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    color: '#666'
                  }}>
                    {profile.first_name ? profile.first_name.charAt(0) : ''}
                    {profile.last_name ? profile.last_name.charAt(0) : ''}
                  </div>
                )}
              </div>
              
              {/* Profile Info Column */}
              <div style={profileInfoStyle}>
                <h3 style={profileNameStyle}>
                  {profile.first_name} {profile.last_name}
                  {profile.license_type && (
                    <span style={licenseBadgeStyle}>{profile.license_type}</span>
                  )}
                </h3>
                
                <div style={profileDetailsGridStyle}>
                  {/* Left column */}
                  <div>
                    {profile.specialties && (
                      <p style={profileDetailItemStyle}>
                        <strong>Specialties:</strong><br />
                        {profile.specialties}
                      </p>
                    )}
                    
                    {profile.years_experience && (
                      <p style={profileDetailItemStyle}>
                        <strong>Experience:</strong> {profile.years_experience} years
                      </p>
                    )}
                  </div>
                  
                  {/* Right column */}
                  <div>
                    {profile.education && (
                      <p style={profileDetailItemStyle}>
                        <strong>Education:</strong><br />
                        {profile.education}
                      </p>
                    )}
                    
                    {profile.client_demographics && (
                      <p style={profileDetailItemStyle}>
                        <strong>Demographics:</strong><br />
                        {profile.client_demographics}
                      </p>
                    )}
                  </div>
                </div>
                
                <div style={acceptingClientsContainerStyle}>
                  <div 
                    style={{
                      ...acceptingClientsIndicatorStyle,
                      backgroundColor: profile.accepting_clients ? '#4CAF50' : '#F44336'
                    }}
                  ></div>
                  <span style={acceptingClientsTextStyle}>
                    {profile.accepting_clients ? 'Accepting new clients' : 'Not accepting new clients'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* NEW: TEAM CARD */}
        <TeamCard teamMembers={teamMembers} />
      </div>

      {/* STATS CARDS */}
      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', marginTop: '2rem' }}>
        {/* CARD #1: Last Week */}
        <div
          style={{
            ...cardStyle,
            backgroundColor: lastWeekObj.background,
            color: textColor
          }}
        >
          <h3 style={cardTitle}>Sessions Last Week</h3>
          <div style={cardNumber}>{lastWeekCount}</div>
          <p style={cardText}>
            Goal: {goals.weekly_session_goal || 0} | Bonus:{' '}
            {goals.weekly_session_bonus || 0}
          </p>
          {lastWeekObj.showEmoji && <div style={emojiStyle}>🎉</div>}
        </div>

        {/* CARD #2: This Week */}
        <div
          style={{
            ...cardStyle,
            backgroundColor: thisWeekObj.background,
            color: textColor
          }}
        >
          <h3 style={cardTitle}>This Week Sessions</h3>
          <div style={cardNumber}>
            {thisWeekCount} / {goals.weekly_session_goal || 0}
          </div>
          <div style={progressBarContainer}>
            <div
              style={{
                ...progressBarFill,
                width: `${progressPercent}%`
              }}
            />
          </div>
          {thisWeekObj.showEmoji && <div style={emojiStyle}>🎉</div>}
        </div>

        {/* CARD #3: Running Avg */}
        <div
          style={{
            ...cardStyle,
            backgroundColor: avgObj.background,
            color: textColor
          }}
        >
          <h3 style={cardTitle}>Running Weekly Avg</h3>
          <div style={cardNumber}>{monthlyAvgCount}</div>
          <p style={cardText}>Last 4 weeks</p>
          {avgObj.showEmoji && <div style={emojiStyle}>🎉</div>}
        </div>

        {/* CARD #4: Debts (Clickable) */}
        <div
          style={{
            ...cardStyle,
            backgroundColor: debtBg,
            color: textColor,
            cursor: 'pointer'
          }}
          onClick={() => navigate('/therapist/debt')}
        >
          <h3 style={cardTitle}>Debts</h3>
          <div style={cardNumber}>Sessions: {debtCount}</div>
          <div style={cardNumber}>Amount: ${safeDebtTotal.toFixed(2)}</div>
        </div>

        {/* CARD #5: No Shows */}
        <div
          style={{
            ...cardStyle,
            backgroundColor: noShowWeekObj.background,
            color: textColor
          }}
        >
          <h3 style={cardTitle}>No Shows</h3>
          <div style={cardNumber}>{noShowWeekCount} | {noShowMonthCount}</div>
          <p style={cardText}>Weekly | Monthly</p>
        </div>

        {/* CARD #6: Cancellations */}
        <div
          style={{
            ...cardStyle,
            backgroundColor: cancelWeekObj.background,
            color: textColor
          }}
        >
          <h3 style={cardTitle}>Cancellations</h3>
          <div style={cardNumber}>{cancelWeekCount} | {cancelMonthCount}</div>
          <p style={cardText}>Weekly | Monthly</p>
        </div>
      </div>
    </div>
  );
}

// NEW: PROFILE MODAL COMPONENT
function ProfileEditModal({ profile, onClose }) {
  const [formData, setFormData] = useState({
    photoUrl: profile.photo_url || '',
    licenseNumber: profile.license_number || '',
    licenseType: profile.license_type || '',
    education: profile.education || '',
    clientDemographics: profile.client_demographics || '',
    acceptingClients: profile.accepting_clients !== undefined ? profile.accepting_clients : true,
    unavailableReason: profile.unavailable_reason || '',
    bio: profile.bio || '',
    interests: profile.interests || '',
    specialties: profile.specialties || '',
    yearsExperience: profile.years_experience || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess(false);

    try {
      await API.put('/therapist/profile', formData);
      setSaveSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveError('Error saving profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <h2 style={{ marginBottom: '1.5rem' }}>Edit Your Profile</h2>
        <form onSubmit={handleSubmit}>
          <div style={formGridStyle}>
            <div style={formColumnStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>Profile Photo URL</label>
                <input
                  type="text"
                  name="photoUrl"
                  value={formData.photoUrl}
                  onChange={handleInputChange}
                  style={inputStyle}
                  placeholder="https://example.com/your-photo.jpg"
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>License Number</label>
                <input
                  type="text"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleInputChange}
                  style={inputStyle}
                  placeholder="e.g., 12345-A"
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>License Type</label>
                <input
                  type="text"
                  name="licenseType"
                  value={formData.licenseType}
                  onChange={handleInputChange}
                  style={inputStyle}
                  placeholder="e.g., LCSW, LPC, LMFT, PhD, etc."
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Years of Experience</label>
                <input
                  type="number"
                  name="yearsExperience"
                  value={formData.yearsExperience}
                  onChange={handleInputChange}
                  style={inputStyle}
                  min="0"
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Education</label>
                <textarea
                  name="education"
                  value={formData.education}
                  onChange={handleInputChange}
                  style={{ ...inputStyle, minHeight: '80px' }}
                  placeholder="e.g., MSW from University of State, 2015"
                />
              </div>
            </div>

            <div style={formColumnStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>Specialties</label>
                <textarea
                  name="specialties"
                  value={formData.specialties}
                  onChange={handleInputChange}
                  style={{ ...inputStyle, minHeight: '80px' }}
                  placeholder="e.g., Anxiety, Depression, PTSD"
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Client Demographics</label>
                <textarea
                  name="clientDemographics"
                  value={formData.clientDemographics}
                  onChange={handleInputChange}
                  style={{ ...inputStyle, minHeight: '80px' }}
                  placeholder="e.g., Adults 18+, Teenagers, Couples"
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Bio</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  style={{ ...inputStyle, minHeight: '120px' }}
                  placeholder="A brief bio about yourself and your approach to therapy..."
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Personal Interests</label>
                <textarea
                  name="interests"
                  value={formData.interests}
                  onChange={handleInputChange}
                  style={{ ...inputStyle, minHeight: '80px' }}
                  placeholder="e.g., Hiking, Cooking, Reading"
                />
              </div>
            </div>
          </div>

          <div style={formGroupStyle}>
            <div style={checkboxContainerStyle}>
              <input
                type="checkbox"
                name="acceptingClients"
                checked={formData.acceptingClients}
                onChange={handleInputChange}
                id="acceptingClients"
              />
              <label htmlFor="acceptingClients" style={{ marginLeft: '8px' }}>
                Currently accepting new clients
              </label>
            </div>
          </div>

          {!formData.acceptingClients && (
            <div style={formGroupStyle}>
              <label style={labelStyle}>Reason not accepting new clients</label>
              <textarea
                name="unavailableReason"
                value={formData.unavailableReason}
                onChange={handleInputChange}
                style={{ ...inputStyle, minHeight: '80px' }}
                placeholder="Please explain why you're not accepting new clients..."
              />
            </div>
          )}

          {saveError && (
            <div style={errorMessageStyle}>{saveError}</div>
          )}

          {saveSuccess && (
            <div style={successMessageStyle}>Profile saved successfully!</div>
          )}

          <div style={buttonContainerStyle}>
            <button 
              type="button" 
              onClick={onClose}
              style={{ ...buttonStyle, backgroundColor: '#6c757d' }}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              style={buttonStyle}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// New style for profile and team cards row
const cardsRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '2rem',
  marginBottom: '2rem'
};

// NEW PROFILE CARD STYLES
const profileCardStyle = {
  padding: '1.5rem',
  borderRadius: '12px',
  backgroundColor: 'white',
  boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  border: '1px solid #e0e0e0',
  height: '100%',
  ':hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.15)'
  }
};

const profileHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1.5rem',
  borderBottom: '1px solid #eee',
  paddingBottom: '0.75rem'
};

const profileTitleStyle = {
  fontSize: '1.4rem',
  margin: 0
};

const editProfileIndicatorStyle = {
  fontSize: '0.9rem',
  color: '#4CAF50',
  fontWeight: 'bold'
};

const profileContentContainerStyle = {
  display: 'flex',
  gap: '1.5rem',
  flex: 1
};

const profilePhotoContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
};

const profilePhotoStyle = {
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  objectFit: 'cover',
  border: '3px solid #4CAF50'
};

const profileInfoStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
};

const profileNameStyle = {
  fontSize: '1.6rem',
  margin: 0,
  marginBottom: '1rem',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap'
};

const licenseBadgeStyle = {
  fontSize: '0.9rem',
  backgroundColor: '#e0e0e0',
  color: '#333',
  padding: '0.2rem 0.6rem',
  borderRadius: '4px',
  marginLeft: '0.75rem',
  fontWeight: 'normal'
};

const profileDetailsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '1rem',
  flex: 1
};

const profileDetailItemStyle = {
  fontSize: '0.9rem',
  margin: '0.5rem 0'
};

const acceptingClientsContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  marginTop: '1rem',
  padding: '0.5rem',
  borderRadius: '4px',
  backgroundColor: '#f5f5f5'
};

const acceptingClientsIndicatorStyle = {
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  marginRight: '8px'
};

const acceptingClientsTextStyle = {
  fontSize: '0.9rem',
  fontWeight: 'bold'
};

// STYLES
const popupStyle = {
  position: 'fixed',
  top: '20%',
  left: '30%',
  right: '30%',
  background: '#fff',
  border: '2px solid #ccc',
  padding: '1rem',
  zIndex: 999
};

const cardStyle = {
  borderRadius: '8px',
  padding: '1.25rem',
  textAlign: 'center',
  boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
  transition: 'background-color 0.4s ease',
  position: 'relative',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
};

const cardTitle = {
  fontSize: '1.25rem',
  margin: 0,
  marginBottom: '0.5rem'
};

const cardNumber = {
  fontSize: '1.6rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem'
};

const cardText = {
  margin: 0,
  fontSize: '0.9rem'
};

const emojiStyle = {
  position: 'absolute',
  bottom: '8px',
  right: '8px',
  fontSize: '1.7rem'
};

const progressBarContainer = {
  width: '100%',
  background: '#ccc',
  height: '20px',
  borderRadius: '10px',
  marginTop: '0.5rem'
};

const progressBarFill = {
  height: '100%',
  backgroundColor: 'blue',
  borderRadius: '10px',
  transition: 'width 0.4s ease'
};

// MODAL STYLES
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
};

const modalContentStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '2rem',
  width: '80%',
  maxWidth: '900px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
};

const formGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '1.5rem'
};

const formColumnStyle = {
  display: 'flex',
  flexDirection: 'column'
};

const formGroupStyle = {
  marginBottom: '1.5rem'
};

const labelStyle = {
  display: 'block',
  marginBottom: '0.5rem',
  fontWeight: 'bold',
  fontSize: '0.9rem'
};

const inputStyle = {
  width: '100%',
  padding: '0.75rem',
  borderRadius: '4px',
  border: '1px solid #ddd',
  fontSize: '1rem'
};

const checkboxContainerStyle = {
  display: 'flex',
  alignItems: 'center'
};

const buttonContainerStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '1rem',
  marginTop: '2rem'
};

const buttonStyle = {
  padding: '0.75rem 1.5rem',
  backgroundColor: '#4CAF50',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  fontSize: '1rem',
  cursor: 'pointer',
  transition: 'background-color 0.2s'
};

const errorMessageStyle = {
  backgroundColor: '#f8d7da',
  color: '#721c24',
  padding: '0.75rem',
  borderRadius: '4px',
  marginBottom: '1rem'
};

const successMessageStyle = {
  backgroundColor: '#d4edda',
  color: '#155724',
  padding: '0.75rem',
  borderRadius: '4px',
  marginBottom: '1rem'
};

export default TherapistDashboard;